/** @jsx jsx */
import { Box, Button, CompanyAvatar, Flex, Heading, Paragraph, Stack } from "@eworkit/iris";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import sanitize from "sanitize-html";
import { jsx } from "theme-ui";

import { ResponsiveCard } from "@/components/ResponsiveCard";
import { ILegalEntityShort } from "@/data/public-profiles";
import { router } from "@/views/router";

interface PublicProfileCardProps {
  legalEntity: ILegalEntityShort;
}

export const PublicProfileCard: FC<PublicProfileCardProps> = ({ legalEntity }) => {
  const { t } = useTranslation("public");

  const publicProfilePath = router.get("public-client.details", { slug: legalEntity.publicProfileSlug }).path;

  return (
    <ResponsiveCard sx={{ position: "relative" }}>
      <Box
        sx={{
          height: [74, null, null, 148],
          width: "100%",
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          backgroundImage: `url(${legalEntity.backgroundImagePublicUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          borderRadius: [null, null, null, "8px 8px 0 0"],
        }}
      />

      <Box sx={{ pt: [24, null, null, 80] }}>
        <Stack gap="md">
          <Stack gap="sm">
            <Flex sx={{ zIndex: 1 }}>
              <Box backgroundColor="n0" borderRadius="md" sx={{ p: [2, null, null, "xxs"] }}>
                <CompanyAvatar
                  size={["lg", null, null, "xl"]}
                  img={legalEntity.logoPublicUrl ?? undefined}
                  name={legalEntity.publicProfileName ?? undefined}
                />
              </Box>
            </Flex>

            <Heading size="h-md">{legalEntity.publicProfileName}</Heading>
            {legalEntity.aboutUs && (
              <Paragraph
                sx={{
                  lineClamp: [5, null, null, 3],
                  WebkitLineClamp: [5, null, null, 3],
                  display: "-webkit-box",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  WebkitBoxOrient: "vertical",
                }}>
                {sanitize(legalEntity.aboutUs, { allowedTags: [], allowedAttributes: {} })}
              </Paragraph>
            )}
          </Stack>

          <Box>
            <Button
              sx={{ display: ["block", null, null, "inline-block"] }}
              as={Link}
              to={publicProfilePath}
              variant="secondary">
              {t("CLIENT_PAGE.BUTTON_VIEW_PROFILE")}
            </Button>
          </Box>
        </Stack>
      </Box>
    </ResponsiveCard>
  );
};
